import {
  Check, ChevronLeft, ChevronRight, Disc3, ExternalLink, Headphones, Heart, Link2,
  LoaderCircle, LogOut, Mail, Music2, Plus, RefreshCw, Save, ShieldCheck, Trash2,
  UserRound
} from 'lucide-react';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, patch, post } from '../api';
import { useAuth } from '../App';
import PageMotion from '../components/PageMotion';
import { date, number } from '../format';
import type { ArtistCatalog, ArtistWithReleases, User } from '../types';

const emptyCatalog: ArtistCatalog = { artists: [] };

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [catalog, setCatalog] = useState<ArtistCatalog>(emptyCatalog);
  const [selectedArtistId, setSelectedArtistId] = useState<number | null>(null);
  const [artistUrl, setArtistUrl] = useState('');
  const [artistLoading, setArtistLoading] = useState(true);
  const [busyArtistId, setBusyArtistId] = useState<number | 'new' | null>(null);
  const [artistError, setArtistError] = useState('');
  const releaseCarousel = useRef<HTMLDivElement>(null);
  const carouselAnimation = useRef<number | null>(null);
  const [carouselEdges, setCarouselEdges] = useState({ start: true, end: false });

  const selectedArtist = useMemo<ArtistWithReleases | null>(() => (
    catalog.artists.find((artist) => artist.id === selectedArtistId)
      || catalog.artists[0]
      || null
  ), [catalog.artists, selectedArtistId]);

  useEffect(() => {
    api<ArtistCatalog>('/profile/artists')
      .then((value) => {
        setCatalog(value);
        setSelectedArtistId(value.artists[0]?.id || null);
      })
      .catch((reason) => setArtistError(reason instanceof Error ? reason.message : 'Не удалось загрузить артистов'))
      .finally(() => setArtistLoading(false));
  }, []);

  useEffect(() => {
    const carousel = releaseCarousel.current;
    if (!carousel) return;
    carousel.scrollLeft = 0;
    const updateEdges = () => setCarouselEdges({
      start: carousel.scrollLeft <= 2,
      end: carousel.scrollWidth <= carousel.clientWidth + 2
        || carousel.scrollLeft + carousel.clientWidth >= carousel.scrollWidth - 2
    });
    const frame = window.requestAnimationFrame(updateEdges);
    carousel.addEventListener('scroll', updateEdges, { passive: true });
    window.addEventListener('resize', updateEdges);
    return () => {
      window.cancelAnimationFrame(frame);
      if (carouselAnimation.current !== null) window.cancelAnimationFrame(carouselAnimation.current);
      carousel.removeEventListener('scroll', updateEdges);
      window.removeEventListener('resize', updateEdges);
    };
  }, [selectedArtist?.id, selectedArtist?.releases.length]);

  const scrollReleases = (direction: -1 | 1) => {
    const carousel = releaseCarousel.current;
    if (!carousel) return;
    const cards = Array.from(carousel.children) as HTMLElement[];
    const firstCard = cards[0];
    if (!firstCard) return;
    if (carouselAnimation.current !== null) window.cancelAnimationFrame(carouselAnimation.current);

    const gap = Number.parseFloat(window.getComputedStyle(carousel).columnGap) || 0;
    const cardStep = firstCard.getBoundingClientRect().width + gap;
    const visibleCards = Math.max(1, Math.round((carousel.clientWidth + gap) / cardStep));
    const currentIndex = Math.round(carousel.scrollLeft / cardStep);
    const targetIndex = Math.max(0, Math.min(cards.length - 1, currentIndex + direction * visibleCards));
    const target = Math.max(0, Math.min(carousel.scrollWidth - carousel.clientWidth, targetIndex * cardStep));
    const start = carousel.scrollLeft;
    const distance = target - start;
    const duration = 720;
    let startedAt: number | null = null;

    carousel.style.scrollSnapType = 'none';
    const animate = (timestamp: number) => {
      if (startedAt === null) startedAt = timestamp;
      const progress = Math.min(1, (timestamp - startedAt) / duration);
      const eased = progress < .5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      carousel.scrollLeft = start + distance * eased;
      if (progress < 1) carouselAnimation.current = window.requestAnimationFrame(animate);
      else {
        carousel.style.scrollSnapType = '';
        carouselAnimation.current = null;
      }
    };
    carouselAnimation.current = window.requestAnimationFrame(animate);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    const form = new FormData(event.currentTarget);
    try {
      const result = await patch<{ user: User }>('/profile', { name: form.get('name') });
      setUser(result.user);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось сохранить');
    }
  };

  const addArtist = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setArtistError('');
    setBusyArtistId('new');
    try {
      const value = await post<ArtistCatalog>('/profile/artists', { artist_url: artistUrl });
      setCatalog(value);
      setSelectedArtistId(value.selected_artist_id || value.artists[0]?.id || null);
      setArtistUrl('');
    } catch (reason) {
      setArtistError(reason instanceof Error ? reason.message : 'Не удалось получить релизы артиста');
    } finally {
      setBusyArtistId(null);
    }
  };

  const refreshArtist = async (artist: ArtistWithReleases) => {
    setArtistError('');
    setBusyArtistId(artist.id);
    try {
      const value = await post<ArtistCatalog>(`/profile/artists/${artist.id}/refresh`);
      setCatalog(value);
      setSelectedArtistId(value.selected_artist_id || artist.id);
    } catch (reason) {
      setArtistError(reason instanceof Error ? reason.message : 'Не удалось обновить артиста');
    } finally {
      setBusyArtistId(null);
    }
  };

  const removeArtist = async (artist: ArtistWithReleases) => {
    if (!window.confirm(`Удалить артиста «${artist.name}» и сохранённые релизы?`)) return;
    setArtistError('');
    setBusyArtistId(artist.id);
    try {
      const value = await api<ArtistCatalog & { ok: boolean }>(`/profile/artists/${artist.id}`, { method: 'DELETE' });
      setCatalog({ artists: value.artists });
      setSelectedArtistId(value.artists[0]?.id || null);
    } catch (reason) {
      setArtistError(reason instanceof Error ? reason.message : 'Не удалось удалить артиста');
    } finally {
      setBusyArtistId(null);
    }
  };

  const logout = async () => {
    await post('/auth/logout');
    setUser(null);
    navigate('/login');
  };

  return (
    <PageMotion>
      <header className="page-header"><div><span className="eyebrow">Аккаунт</span><h1>Профиль</h1><p>Личные данные, артисты и безопасность.</p></div></header>
      <section className="profile-head">
        <div className="profile-avatar">{user?.name.slice(0, 1).toUpperCase()}</div>
        <div><h2>{user?.name}</h2><p>{user?.email}</p><span>С нами с {user ? date(user.created_at) : '—'}</span></div>
      </section>
      <div className="profile-layout">
        <form className="content-section profile-form" onSubmit={submit}>
          <div className="section-title"><div><span className="eyebrow">Основное</span><h2>Данные профиля</h2></div></div>
          <label>Имя или псевдоним<div className="input-with-icon"><UserRound size={17} /><input name="name" defaultValue={user?.name} required minLength={2} /></div></label>
          <label>Электронная почта<div className="input-with-icon disabled"><Mail size={17} /><input value={user?.email || ''} disabled /></div></label>
          {error && <div className="form-error">{error}</div>}
          <button className="primary-button"><Save size={17} />{saved ? <><Check size={16} /> Сохранено</> : 'Сохранить'}</button>
        </form>
        <aside className="content-section security-panel">
          <span className="service-icon tone-cyan"><ShieldCheck size={20} /></span>
          <h2>Безопасность</h2>
          <p>Сессия хранится в защищённой cookie и недоступна JavaScript-коду страницы.</p>
          <button className="danger-button" onClick={logout}><LogOut size={17} /> Выйти из аккаунта</button>
        </aside>
      </div>

      <section className="content-section artist-profile-section">
        <div className="section-title artist-section-title">
          <div><span className="eyebrow">Яндекс.Музыка</span><h2>Мои артисты</h2><p>{catalog.artists.length ? `${catalog.artists.length} проф. · выберите артиста для просмотра каталога` : 'Добавьте первую карточку артиста'}</p></div>
          {catalog.artists.length > 0 && <span className="artist-count-badge">{catalog.artists.length}</span>}
        </div>

        <form className="artist-link-form" onSubmit={addArtist}>
          <div className="input-with-icon"><Link2 size={17} /><input type="url" value={artistUrl} onChange={(event) => setArtistUrl(event.target.value)} placeholder="https://music.yandex.ru/artist/..." required /></div>
          <button className="primary-button" disabled={busyArtistId !== null}>
            {busyArtistId === 'new' ? <><LoaderCircle size={17} className="spinning" /> Добавляю</> : <><Plus size={17} /> Добавить артиста</>}
          </button>
        </form>
        {artistError && <div className="form-error artist-form-error">{artistError}</div>}

        {artistLoading ? <div className="artist-catalog-loading"><span className="spinner" /> Загружаю артистов</div> : !catalog.artists.length ? (
          <div className="artist-library-empty"><span><Music2 size={27} /></span><div><h3>Артистов пока нет</h3><p>Вставьте ссылку на карточку Яндекс.Музыки.</p></div></div>
        ) : <>
          <div className="artist-library-grid">
            {catalog.artists.map((artist) => {
              const selected = selectedArtist?.id === artist.id;
              const busy = busyArtistId === artist.id;
              return <article className={`artist-library-item ${selected ? 'selected' : ''}`} key={artist.id}>
                <button className="artist-library-select" type="button" onClick={() => setSelectedArtistId(artist.id)}>
                  {artist.cover_url ? <img src={artist.cover_url} alt="" /> : <span><Music2 size={22} /></span>}
                  <div><small>{artist.releases.length} релизов</small><strong>{artist.name}</strong><p>{number(artist.listeners)} слушателей</p></div>
                  <i>{selected ? <Check size={14} /> : null}</i>
                </button>
                <div className="artist-library-actions">
                  <button type="button" title="Обновить релизы" onClick={() => void refreshArtist(artist)} disabled={busyArtistId !== null}><RefreshCw size={15} className={busy ? 'spinning' : ''} /></button>
                  <button type="button" title="Удалить артиста" onClick={() => void removeArtist(artist)} disabled={busyArtistId !== null}><Trash2 size={15} /></button>
                </div>
              </article>;
            })}
          </div>

          {selectedArtist && <div className="selected-artist-catalog" key={selectedArtist.id}>
            <article className="artist-card">
              {selectedArtist.cover_url ? <img src={selectedArtist.cover_url} alt={`Обложка ${selectedArtist.name}`} /> : <span className="artist-cover-fallback"><Music2 size={30} /></span>}
              <div className="artist-card-copy"><span>Выбранный артист</span><h3>{selectedArtist.name}</h3><a href={selectedArtist.artist_url} target="_blank" rel="noreferrer">Открыть в Яндекс.Музыке <ExternalLink size={13} /></a></div>
              <div className="artist-stats">
                <div><Headphones size={17} /><strong>{number(selectedArtist.listeners)}</strong><small>слушателей в месяц</small></div>
                <div><Heart size={17} /><strong>{number(selectedArtist.likes)}</strong><small>лайков артиста</small></div>
                <div><Disc3 size={17} /><strong>{number(selectedArtist.releases.length)}</strong><small>релизов сохранено</small></div>
              </div>
            </article>

            <div className="artist-releases-head"><h3>Релизы {selectedArtist.name}</h3><span>{selectedArtist.releases.length}</span></div>
            {selectedArtist.releases.length ? <div className="artist-release-carousel-shell">
              <button className="artist-carousel-arrow" type="button" aria-label="Предыдущие релизы" onClick={() => scrollReleases(-1)} disabled={carouselEdges.start}><ChevronLeft size={19} /></button>
              <div className="artist-release-carousel" ref={releaseCarousel}>
                {selectedArtist.releases.map((release) => <a className="artist-release-card" href={release.album_url} target="_blank" rel="noreferrer" key={release.album_id}>
                  <div className="release-cover-wrap">
                    {release.cover_url ? <img src={release.cover_url} alt={`Обложка ${release.title}`} loading="lazy" /> : <span><Disc3 size={25} /></span>}
                    <i><ExternalLink size={16} /></i>
                  </div>
                  <div><strong title={release.title}>{release.title}</strong><small>{[release.year, release.track_count ? `${release.track_count} тр.` : ''].filter(Boolean).join(' · ')}</small></div>
                </a>)}
              </div>
              <button className="artist-carousel-arrow" type="button" aria-label="Следующие релизы" onClick={() => scrollReleases(1)} disabled={carouselEdges.end}><ChevronRight size={19} /></button>
            </div> : <div className="catalog-empty"><Disc3 size={22} /><span>У этого артиста пока нет доступных релизов.</span></div>}
          </div>}
        </>}
      </section>
    </PageMotion>
  );
}
